import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  CheckCircle2,
  Clock,
  ArrowRight,
  ShieldCheck,
  Zap,
  PhoneCall,
  FileText,
  Layers,
  BarChart3,
  MessageSquare,
  Lock,
  Award,
  Check,
  Copy,
  Wrench,
  ChevronDown,
  Loader2,
  Presentation,
  Terminal,
  Globe
} from "lucide-react";
import Navbar from "@/components/portfolio/Navbar";
import Footer from "@/components/portfolio/Footer";
import DemoAuditPresentationSection from "./DemoAuditPresentationSection";

export default function AiVisibilityAuditDemoPage() {
  // Form State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    website: "",
    whatsapp: "",
    serviceNiche: "",
    specificQuestion: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    document.title = "Demo: Website AI Visibility Audit — White Spotlight Offer";
    const handleScroll = () => {
      // Show sticky bar after scrolling past 450px
      if (window.scrollY > 450) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg("");

    try {
      const response = await fetch("/api/audit-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setIsSubmitted(true);
      } else {
        setErrorMsg(data.error || "Failed to submit request. Please try again or ping directly on WhatsApp.");
      }
    } catch (err) {
      setErrorMsg("Network error. Please try again or reach out on WhatsApp (+91 8464094525).");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    const el = document.getElementById("order-audit-form");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const copyPhoneNumber = () => {
    navigator.clipboard.writeText("+918464094525");
    setCopiedPhone(true);
    setTimeout(() => setCopiedPhone(false), 2500);
  };

  // 6 Deliverables — Tailored for the White Spotlight Container (2 Rows)
  const deliverables = [
    {
      num: "01",
      icon: FileText,
      badgeColor: "text-blue-700 bg-blue-100/90 border-blue-200",
      iconBg: "bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20",
      cardBg: "bg-gradient-to-b from-blue-50/60 via-white to-white border-blue-200/90 hover:border-blue-400 hover:shadow-lg transition-all",
      footerColor: "text-blue-700",
      title: "AI Technical & Crawl Audit",
      desc: "Full inspection of bot access, robots.txt, JS hydration, and schema graphs with developer-ready fix blueprints."
    },
    {
      num: "02",
      icon: Layers,
      badgeColor: "text-cyan-700 bg-cyan-100/90 border-cyan-200",
      iconBg: "bg-gradient-to-br from-cyan-600 to-teal-600 text-white shadow-md shadow-cyan-500/20",
      cardBg: "bg-gradient-to-b from-cyan-50/60 via-white to-white border-cyan-200/90 hover:border-cyan-400 hover:shadow-lg transition-all",
      footerColor: "text-cyan-700",
      title: "Custom High-Intent Topic Map",
      desc: "Targeted content queries built around your products, structured for ChatGPT and Claude to extract and cite."
    },
    {
      num: "03",
      icon: PhoneCall,
      badgeColor: "text-emerald-700 bg-emerald-100/90 border-emerald-200",
      iconBg: "bg-gradient-to-br from-emerald-600 to-teal-700 text-white shadow-md shadow-emerald-500/20",
      cardBg: "bg-gradient-to-b from-emerald-50/60 via-white to-white border-emerald-200/90 hover:border-emerald-400 hover:shadow-lg transition-all",
      footerColor: "text-emerald-700",
      title: "Two Private 1-on-1 Calls",
      desc: "15-min intake prep call + 30-min Google Meet handover walkthrough to review every recommendation."
    },
    {
      num: "04",
      icon: CheckCircle2,
      badgeColor: "text-purple-700 bg-purple-100/90 border-purple-200",
      iconBg: "bg-gradient-to-br from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-500/20",
      cardBg: "bg-gradient-to-b from-purple-50/60 via-white to-white border-purple-200/90 hover:border-purple-400 hover:shadow-lg transition-all",
      footerColor: "text-purple-700",
      title: "Pre-Publishing Editorial Checklist",
      desc: "A quick editorial standard for your writers before publishing new articles to ensure AI indexing."
    },
    {
      num: "05",
      icon: Sparkles,
      badgeColor: "text-indigo-700 bg-indigo-100/90 border-indigo-200",
      iconBg: "bg-gradient-to-br from-indigo-600 to-blue-600 text-white shadow-md shadow-indigo-500/20",
      cardBg: "bg-gradient-to-b from-indigo-50/60 via-white to-white border-indigo-200/90 hover:border-indigo-400 hover:shadow-lg transition-all",
      footerColor: "text-indigo-700",
      title: "AI Research & Topic Prompts",
      desc: "Battle-tested prompt pack to discover audience questions and test citation readiness independently."
    },
    {
      num: "06",
      icon: BarChart3,
      badgeColor: "text-amber-800 bg-amber-100/90 border-amber-200",
      iconBg: "bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-md shadow-amber-500/20",
      cardBg: "bg-gradient-to-b from-amber-50/60 via-white to-white border-amber-200/90 hover:border-amber-400 hover:shadow-lg transition-all",
      footerColor: "text-amber-800",
      title: "20–30 Tracking Prompts",
      desc: "Benchmark search prompts to test monthly in ChatGPT, Claude, and Gemini to track your citation growth."
    }
  ];

  // 4 Core Website Blockers — Dark Container Style
  const blockers = [
    {
      num: "01",
      tag: "Technical Bot Access",
      title: "Can AI Crawlers Read Your Content?",
      desc: "Firewalls, robots.txt blocks, or client-side JavaScript cause bot crawl timeouts (2–3s limit).",
      check: "Audit: Bot access, render latency & raw HTML"
    },
    {
      num: "02",
      tag: "Audience Intent",
      title: "Does Content Answer Buyer Queries Directly?",
      desc: "Pages written for traditional keywords often miss direct conversational answers required by LLMs.",
      check: "Audit: Conversational relevance & answer extraction"
    },
    {
      num: "03",
      tag: "Factual Density",
      title: "Is Your Content Worth Citing?",
      desc: "AI skips promotional fluff and quotes pages packed with direct definitions, data tables, and clear proof.",
      check: "Audit: Factual chunking & structured comparison tables"
    },
    {
      num: "04",
      tag: "Entity Footprint",
      title: "Do LLMs Recognize Your Authority?",
      desc: "Missing Schema.org graphs and cross-web consensus prevent models from trusting your business.",
      check: "Audit: Schema.org entity graphs & Knowledge Graph signals"
    }
  ];

  // Curated High-Impact FAQs
  const faqs = [
    {
      q: "What exactly do I receive for ₹5,000?",
      a: "You get the complete 6-part package: (1) Full Website AI Diagnostic & Fixes Blueprint, (2) Custom topic list, (3) Two private 1-on-1 calls (15m prep + 30m walkthrough), (4) Team checklist, (5) AI prompt pack, and (6) 20–30 tracking prompts."
    },
    {
      q: "How does payment work? Do I pay upfront?",
      a: "Zero upfront payment. Narendra connects with you on a 15-minute call first to review your website goals and confirm fit. You only pay ₹5,000 via UPI (PhonePe / GPay) or bank transfer after we talk."
    },
    {
      q: "What is the turnaround time?",
      a: "Guaranteed 72 hours. Your countdown begins immediately after our 15-minute intake call is completed."
    },
    {
      q: "Do you make changes on my website, or does my developer?",
      a: "I deliver the complete diagnostic report, prioritized developer action plan, and copy-paste Schema.org templates, and walk through them during our 30-minute Google Meet call. Your internal team implements the fixes."
    },
    {
      q: "What access or logins do I need to share?",
      a: "No logins or admin passwords are required right now. During our prep call, we will coordinate simple read-only (Viewer) access to Google Search Console or server logs if available."
    },
    {
      q: "Can I inspect a sample report before ordering?",
      a: "Yes! You can download our complete 22-slide Demo Presentation Deck (PPTX) right on this page to inspect the exact format and blueprints we deliver."
    }
  ];

  return (
    <div className="min-h-screen bg-[#070a12] text-[#f8fafc] font-sans selection:bg-cyan-500 selection:text-white relative overflow-hidden">
      {/* Background Ambient Glow (Original Dark Style) */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[850px] h-[450px] bg-gradient-to-b from-blue-600/15 via-cyan-500/10 to-transparent blur-[140px]" />
        <div className="absolute top-[40%] right-[-10%] w-[500px] h-[500px] bg-indigo-600/10 blur-[150px]" />
        <div className="absolute bottom-[20%] left-[-10%] w-[500px] h-[500px] bg-cyan-600/10 blur-[150px]" />
      </div>

      {/* Standard Portfolio Navbar (Dark) */}
      <Navbar />

      <main className="relative z-10 pt-28 sm:pt-36 pb-24 sm:pb-32">
        {/* Demo Notification Banner */}
        <div className="max-w-4xl mx-auto mb-6 px-4">
          <div className="p-3 sm:p-3.5 rounded-2xl bg-gradient-to-r from-blue-950/80 via-slate-900 to-cyan-950/80 border border-cyan-500/40 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs shadow-lg backdrop-blur-md">
            <div className="flex items-center gap-2 text-cyan-300 font-mono">
              <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse shrink-0" />
              <span><strong>DEMO PREVIEW:</strong> Dark Theme with White Spotlight Deliverables Container</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to="/ai-visibility-audit/"
                className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white font-mono text-[11px] transition-colors"
              >
                Compare with Live Page →
              </Link>
            </div>
          </div>
        </div>

        {/* =========================================================
            1. HERO SECTION (Original Dark, Punchy, Focused)
           ========================================================= */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-mono uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
            <span>AI Visibility &amp; Generative Engine Audit</span>
          </div>

          <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-white tracking-tight leading-[1.15] font-heading max-w-3xl mx-auto">
            Is Your Website Ready to Be{" "}
            <span className="bg-gradient-to-r from-cyan-400 via-sky-300 to-blue-500 bg-clip-text text-transparent">
              Cited by ChatGPT &amp; Claude?
            </span>
          </h1>

          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            I audit your website&apos;s AI search readiness, deliver an actionable <strong className="text-white">Key Fixes Blueprint</strong> for your developer, and provide a <strong className="text-cyan-300">Free Custom AI Content Plan</strong>.
          </p>

          {/* 3 Value Badges */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xs text-slate-300 font-mono">
            <span className="px-3 py-1 rounded-full bg-slate-900/90 border border-white/10 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-cyan-400" />
              ₹5,000 Flat
            </span>
            <span className="px-3 py-1 rounded-full bg-slate-900/90 border border-white/10 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-cyan-400" />
              72h Turnaround
            </span>
            <span className="px-3 py-1 rounded-full bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 flex items-center gap-1.5 font-medium">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              Pay Only After We Talk
            </span>
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={scrollToForm}
              className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-heading font-bold text-sm shadow-[0_0_30px_rgba(37,99,235,0.4)] hover:shadow-[0_0_40px_rgba(6,182,212,0.6)] transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 group cursor-pointer"
            >
              <span>Order AI Visibility Audit — ₹5,000</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>

            <a
              href="#demo-audit-report"
              className="w-full sm:w-auto px-5 py-3.5 rounded-xl bg-slate-900/90 hover:bg-slate-800 border border-cyan-500/30 hover:border-cyan-400 text-cyan-300 hover:text-white font-heading font-semibold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <Presentation className="w-4 h-4 text-cyan-400" />
              <span>Preview Demo PPT Deck</span>
            </a>
          </div>

          {/* Compact Consultant Pill */}
          <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-2xl bg-slate-900/70 border border-white/10 text-xs backdrop-blur-md text-left">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 text-white flex items-center justify-center font-black text-xs shrink-0">
              KN
            </div>
            <div className="truncate">
              <span className="font-bold text-white font-heading">Kanna Narendra Kumar</span>
              <span className="text-slate-400 ml-1.5 hidden sm:inline">• AEO &amp; SEO Consultant (100% Hand-Crafted)</span>
            </div>
          </div>
        </section>

        {/* =========================================================
            2. SECOND CONTAINER: WHITE SPOTLIGHT (What You Get in 2 Rows)
           ========================================================= */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-20">
          <div className="bg-white text-slate-900 rounded-3xl p-6 sm:p-9 shadow-[0_25px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/20 relative overflow-hidden border-2 border-white/90">
            {/* Top decorative gradient bar */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500" />

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-100">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-mono uppercase tracking-wider mb-2 font-bold">
                  <span>Scope of Work</span>
                  <span>•</span>
                  <span>6 Core Deliverables in 2 Rows</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-950 font-heading">
                  What you get for ₹5,000
                </h2>
                <p className="text-slate-600 text-xs sm:text-sm mt-1 max-w-xl leading-relaxed">
                  A complete, developer-ready diagnostic package and strategy roadmap to win citations in ChatGPT, Claude, and Perplexity.
                </p>
              </div>

              {/* Price Pill */}
              <div className="px-4 py-2.5 rounded-2xl bg-gradient-to-br from-emerald-600 via-teal-700 to-emerald-800 text-white shadow-md shadow-emerald-700/25 flex items-center gap-3 shrink-0 self-start md:self-auto">
                <div>
                  <span className="text-[10px] font-mono uppercase text-emerald-100 block font-bold">Total Investment</span>
                  <span className="text-lg sm:text-xl font-black text-white font-heading">₹5,000 Flat</span>
                </div>
              </div>
            </div>

            {/* Scope Clarity Note */}
            <div className="mt-5 p-3.5 rounded-2xl bg-sky-50 border border-sky-200/90 flex items-start gap-2.5 text-xs text-sky-950">
              <Wrench className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
              <span>
                <strong className="text-slate-900">Clear Execution Scope:</strong> I deliver the diagnostic blueprints and schema templates. <strong>Your developer implements the changes</strong> with full support on our 30-minute handover call.
              </span>
            </div>

            {/* 6 Deliverables in EXACTLY 2 ROWS (3 Columns x 2 Rows) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              {deliverables.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.num}
                    className={`p-5 rounded-2xl border transition-all flex flex-col justify-between ${item.cardBg}`}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${item.iconBg}`}>
                          <Icon className="w-4.5 h-4.5" />
                        </div>
                        <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded-md border ${item.badgeColor}`}>
                          {item.num}
                        </span>
                      </div>
                      <h3 className="text-sm sm:text-base font-bold text-slate-900 font-heading mb-1.5 leading-snug">
                        {item.title}
                      </h3>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                    <div className={`mt-4 pt-2.5 border-t border-slate-100 flex items-center gap-1.5 text-[11px] font-mono font-semibold ${item.footerColor}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                      <span>Included in ₹5,000</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================
            3. THE DIAGNOSTIC (Original Dark Background)
           ========================================================= */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-20">
          <div className="bg-[#0b0f19] border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-9 shadow-2xl">
            {/* Header */}
            <div className="max-w-2xl mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-purple-500/10 text-purple-400 text-xs font-mono uppercase tracking-wider mb-2">
                <span>The Diagnostic</span>
                <span>•</span>
                <span>Google vs. AI Search</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-heading">
                Why does Google rank you, but AI skips your website?
              </h2>
              <p className="text-slate-300 text-xs sm:text-sm mt-1.5 leading-relaxed">
                Google crawls for hours and ranks links by backlinks. AI models (ChatGPT, Claude, Perplexity) operate with a strict <strong className="text-white">2–3s timeout</strong>. If your content is buried behind client-side rendering or lacks clear entity data, AI skips your domain.
              </p>
            </div>

            {/* 4 Blockers Grid (2x2 Clean Layout) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {blockers.map((item) => (
                <div
                  key={item.num}
                  className="p-4 sm:p-5 rounded-2xl bg-slate-900/60 border border-white/[0.08] hover:border-purple-500/30 transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2 py-0.5 rounded-full">
                        {item.tag}
                      </span>
                      <span className="text-xs font-mono text-slate-500 font-bold">{item.num}</span>
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-white font-heading mb-1.5 leading-snug">
                      {item.title}
                    </h3>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                  <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center gap-1.5 text-[11px] text-slate-400 font-mono">
                    <CheckCircle2 className="w-3 h-3 text-purple-400 shrink-0" />
                    <span>{item.check}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Solution Bar */}
            <div className="mt-5 p-4 rounded-xl bg-gradient-to-r from-purple-950/30 via-slate-900 to-slate-950 border border-purple-500/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-purple-500/15 border border-purple-500/25 text-purple-400 flex items-center justify-center shrink-0">
                  <Zap className="w-4 h-4" />
                </div>
                <p className="text-xs sm:text-sm text-slate-200">
                  <strong className="text-white">Uncover exact crawl bottlenecks:</strong> Hand-inspected with prioritized fixes.
                </p>
              </div>
              <button
                onClick={scrollToForm}
                className="w-full sm:w-auto px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-heading font-bold text-xs uppercase tracking-wider transition-colors shrink-0 flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <span>Fix AI Visibility</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </section>

        {/* =========================================================
            4. DEMO AUDIT PRESENTATION PREVIEW (PPTX)
           ========================================================= */}
        <div className="mt-14 sm:mt-18">
          <DemoAuditPresentationSection onOrderClick={scrollToForm} />
        </div>

        {/* =========================================================
            5. HOW IT WORKS (Original Dark Background)
           ========================================================= */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-20">
          <div className="bg-[#0b0f19] border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-9 shadow-2xl">
            {/* Header */}
            <div className="max-w-2xl mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-sky-500/10 text-sky-400 text-xs font-mono uppercase tracking-wider mb-2">
                <Clock className="w-3.5 h-3.5 text-sky-400" />
                <span>Simple 3-Step Process</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-heading">
                How the audit process works
              </h2>
              <p className="text-slate-300 text-xs sm:text-sm mt-1 leading-relaxed">
                Fast, friction-free turnaround with zero upfront risk.
              </p>
            </div>

            {/* 3 Steps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Step 1 */}
              <div className="p-4 sm:p-5 rounded-2xl bg-slate-900/60 border border-white/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-7 h-7 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 font-mono text-xs font-bold flex items-center justify-center">
                      01
                    </span>
                    <span className="text-[10px] font-mono text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded-full">
                      1 Minute
                    </span>
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-white font-heading mb-1">Submit Details</h3>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Enter your website URL and core offerings below. Zero upfront payment required to submit.
                  </p>
                </div>
                <div className="mt-4 pt-2.5 border-t border-white/5 text-[10px] text-slate-400 font-mono">
                  No payment or card required
                </div>
              </div>

              {/* Step 2 */}
              <div className="p-4 sm:p-5 rounded-2xl bg-emerald-950/20 border border-emerald-500/30 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-mono text-xs font-bold flex items-center justify-center">
                      02
                    </span>
                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full font-semibold">
                      15-Min Call
                    </span>
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-white font-heading mb-1">Alignment Call</h3>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Quick 15-min call to align on your business model and target buyers. <strong className="text-emerald-400">Pay ₹5,000 only after we talk.</strong>
                  </p>
                </div>
                <div className="mt-4 pt-2.5 border-t border-emerald-500/20 text-[10px] text-emerald-300 font-mono font-medium">
                  Zero-Risk Guarantee
                </div>
              </div>

              {/* Step 3 */}
              <div className="p-4 sm:p-5 rounded-2xl bg-slate-900/60 border border-white/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="w-7 h-7 rounded-full bg-sky-500/20 border border-sky-500/40 text-sky-300 font-mono text-xs font-bold flex items-center justify-center">
                      03
                    </span>
                    <span className="text-[10px] font-mono text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded-full font-semibold">
                      72h Delivery
                    </span>
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-white font-heading mb-1">Handover Walkthrough</h3>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Receive your Full Audit, Developer Blueprint, Content Plan, and Prompt Packs with a 30-min Google Meet session.
                  </p>
                </div>
                <div className="mt-4 pt-2.5 border-t border-white/5 text-[10px] text-slate-400 font-mono">
                  Includes 30-min strategy call
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="mt-5 p-3 rounded-xl bg-slate-900/80 border border-white/10 flex items-center gap-2.5 text-xs text-slate-300">
              <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" />
              <span>
                <strong className="text-white">No technical setup needed today:</strong> If you have Google Search Console, we&apos;ll simply guide you on granting read-only (Viewer) access during our call.
              </span>
            </div>
          </div>
        </section>

        {/* =========================================================
            6. ORDER & INTAKE FORM (Original Dark Background)
           ========================================================= */}
        <section id="order-audit-form" className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-20 scroll-mt-24">
          <div className="bg-[#0b0f19] border-2 border-cyan-500/30 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-[0_0_50px_rgba(6,182,212,0.12)] relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="text-center pb-5 border-b border-white/[0.08]">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-mono uppercase tracking-wider mb-2">
                <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
                <span>₹5,000 Flat · Guaranteed 72h</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-extrabold text-white font-heading">
                Order Your AI Visibility Audit
              </h2>
              <p className="text-xs sm:text-sm text-slate-300 mt-1">
                Zero upfront payment. Pay only after our 15-minute alignment call.
              </p>
            </div>

            {isSubmitted ? (
              <div className="py-7 text-center space-y-4 animate-in fade-in zoom-in-95 duration-200">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto shadow-lg">
                  <CheckCircle2 className="w-6 h-6" />
                </div>

                <div className="space-y-1">
                  <h3 className="text-lg sm:text-xl font-bold text-white font-heading">
                    Audit Request Received!
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-300 max-w-sm mx-auto leading-relaxed">
                    Thank you, <span className="text-white font-semibold">{formData.name}</span>! Narendra has received your domain <span className="text-cyan-300 font-mono">({formData.website})</span>.
                  </p>
                </div>

                <div className="max-w-sm mx-auto p-3.5 rounded-xl bg-slate-900/90 border border-white/10 text-left text-xs space-y-1.5 font-mono text-slate-300">
                  <p className="text-emerald-400 font-bold uppercase text-[10px] flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> Next Steps:
                  </p>
                  <p>1. Narendra reviews your website.</p>
                  <p>2. You receive an invite for our 15-min call.</p>
                  <p>3. Pay ₹5,000 only after we talk.</p>
                </div>

                <div className="pt-1">
                  <a
                    href={`https://wa.me/918464094525?text=${encodeURIComponent(
                      `Hi Narendra, I just submitted my domain ${formData.website} for the ₹5,000 Website AI Visibility Audit. Looking forward to connecting!`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-bold text-xs uppercase tracking-wider shadow-lg transition-all"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>Ping on WhatsApp</span>
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="mt-5 space-y-3.5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-1">
                      Your Name <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Rahul Sharma"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 text-white text-xs sm:text-sm placeholder:text-slate-500 outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-1">
                      Email Address <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="rahul@company.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 text-white text-xs sm:text-sm placeholder:text-slate-500 outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-1">
                      Website URL <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="website"
                      required
                      placeholder="https://yourcompany.com"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 text-white text-xs sm:text-sm placeholder:text-slate-500 outline-none font-mono transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-1">
                      WhatsApp / Phone <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      type="tel"
                      name="whatsapp"
                      required
                      placeholder="+91 98765 43210"
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 text-white text-xs sm:text-sm placeholder:text-slate-500 outline-none font-mono transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-1">
                    Core Service or Product <span className="text-cyan-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="serviceNiche"
                    required
                    placeholder="e.g. B2B SaaS for HR automation / Financial compliance"
                    value={formData.serviceNiche}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 text-white text-xs sm:text-sm placeholder:text-slate-500 outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-slate-300 mb-1">
                    Specific Question? <span className="text-slate-500">(Optional)</span>
                  </label>
                  <textarea
                    name="specificQuestion"
                    rows="2"
                    placeholder="e.g. We rank on Google but don't show up when asking ChatGPT."
                    value={formData.specificQuestion}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 rounded-xl bg-slate-900/90 border border-white/10 focus:border-cyan-400 text-white text-xs sm:text-sm placeholder:text-slate-500 outline-none resize-none transition-colors"
                  />
                </div>

                {errorMsg && (
                  <p className="text-xs text-rose-400 font-mono bg-rose-500/10 border border-rose-500/20 p-2.5 rounded-xl">
                    {errorMsg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-heading font-bold text-xs sm:text-sm shadow-[0_0_25px_rgba(37,99,235,0.35)] hover:shadow-[0_0_35px_rgba(6,182,212,0.5)] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Website for AI Audit — ₹5,000</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>

                <p className="text-center text-[11px] text-slate-400 font-mono pt-0.5">
                  <span className="text-emerald-400 font-semibold">Zero upfront payment</span> · Pay only after our 15-minute call
                </p>
              </form>
            )}
          </div>
        </section>

        {/* =========================================================
            7. FREQUENTLY ASKED QUESTIONS (Original Dark Accordions)
           ========================================================= */}
        <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 sm:mt-20">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-extrabold text-white font-heading">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-2.5">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="rounded-xl bg-slate-900/60 border border-white/10 overflow-hidden transition-colors"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full p-3.5 sm:p-4 text-left flex items-center justify-between gap-3 focus:outline-none"
                >
                  <span className="text-xs sm:text-sm font-bold text-white font-heading">
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 text-cyan-400 shrink-0 transition-transform duration-200 ${
                      openFaq === idx ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaq === idx && (
                  <div className="px-3.5 sm:px-4 pb-3.5 sm:pb-4 text-xs text-slate-300 leading-relaxed border-t border-white/5 pt-2.5">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* =========================================================
            8. DIRECT CONTACT (Original Dark Container)
           ========================================================= */}
        <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 text-center">
          <div className="p-4 sm:p-5 rounded-2xl bg-slate-950 border border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-left">
              <h3 className="text-sm sm:text-base font-bold text-white font-heading">
                Have questions before ordering?
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Chat with Narendra directly: <strong className="text-white">+91 8464094525</strong>
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <a
                href="https://wa.me/918464094525?text=Hi%20Narendra,%20I%20have%20a%20question%20regarding%20the%20Website%20AI%20Visibility%20Audit."
                target="_blank"
                rel="noreferrer"
                className="px-3.5 py-1.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-300 font-mono text-xs font-bold transition-colors flex items-center gap-1.5"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>WhatsApp</span>
              </a>

              <button
                onClick={copyPhoneNumber}
                className="p-1.5 rounded-xl bg-slate-900 border border-white/10 text-slate-300 hover:text-white transition-colors cursor-pointer"
                title="Copy phone number"
                aria-label="Copy phone number"
              >
                {copiedPhone ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* =========================================================
          STICKY BOTTOM BAR (Mobile & Desktop)
         ========================================================= */}
      <AnimatePresence>
        {showStickyBar && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-4 left-4 right-4 z-40 max-w-2xl mx-auto"
          >
            <div className="p-3 rounded-2xl bg-[#0b0f19]/95 backdrop-blur-xl border border-cyan-500/40 shadow-2xl flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 flex items-center justify-center shrink-0">
                  <Sparkles className="w-3.5 h-3.5" />
                </div>
                <div className="truncate">
                  <p className="text-xs font-bold text-white truncate font-heading">
                    Website AI Visibility Audit
                  </p>
                  <p className="text-[10px] text-cyan-300 font-mono truncate">
                    ₹5,000 Flat · 72h Turnaround · Pay After Prep Call
                  </p>
                </div>
              </div>

              <button
                onClick={scrollToForm}
                className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-heading font-bold text-xs uppercase tracking-wider shadow-lg transition-all shrink-0 cursor-pointer"
              >
                Order — ₹5,000
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
